/**
 * upload-prep.js — הכנת התמונה לפני שליחה ל-Lambda.
 * ⚠️ קוד פיתוח בלבד. לא נוגע ב-Shopify ולא באתר החי.
 *
 * ═══ הבעיה שזה פותר ═══
 *
 * AWS Lambda Function URL מוגבל ל-6,291,456 בייט לבקשה ולתשובה.
 * גוף הבקשה מקודד base64 בדרך, מה שמנפח אותו בכ-33%. כלומר קובץ
 * מעל ~4.4MB נדחה עוד לפני שהקוד בענן רץ בכלל:
 *
 *   413 — "7622092 byte payload is too large for the RequestResponse
 *          invocation type (limit 6291456 bytes)"
 *
 * ═══ אותה בעיה קיימת באתר החי ═══
 *
 * pillow-engine.js מקטין לפני ההעלאה (compressImage, שורה ~371), אבל
 * שורה 378 קובעת outputType = 'image/png'. ההערה מעליה עוד אומרת
 * "Convert any image to JPG", והשורה שמייצרת JPEG מבוטלת בהערה.
 * PNG של תצלום ב-3000×3000 שוקל בקלות 7MB ומעלה, ו-QUALITY בכלל
 * לא חל על PNG — toBlob מתעלם ממנו בפורמט הזה.
 * המשמעות: לקוח עם תמונת טלפון גדולה מקבל את אותה שגיאה באתר.
 *
 * ═══ מה עושים כאן ═══
 *
 * מקטינים לרוחב/גובה מרבי, מקודדים JPEG, ואם עדיין כבד — מורידים
 * איכות ואז מידות עד שנכנסים לתקציב. לולאה עם תנאי עצירה ודאי,
 * לא ניחוש.
 *
 * למה JPEG ולא PNG: הרקע מוסר בצד השרת, ולכן ערוץ אלפא בקלט הוא
 * חסר משמעות — הוא רק מנפח את הקובץ. התשובה חוזרת PNG עם אלפא.
 */
(function (global) {
  'use strict';

  const HARD_LIMIT = 6291456;             // המגבלה של Lambda
  const BUDGET = Math.floor(HARD_LIMIT / 1.34) - 200000;   // ~4.5MB אחרי ניפוח base64
  const MAX_DIM = 2400;
  const QUALITIES = [0.9, 0.82, 0.72, 0.6];

  // ═══ HEIC (אלעד, 10.9) ═══
  // אייפון מצלם HEIC. Safari יודע לפתוח אותו ב-<img>, אבל ה-Lambda (Pillow
  // בלי pillow-heif) לא — ולכן HEIC *קטן*, שעבר ב"מסלול המהיר" כמו שהוא,
  // נכשל בצד השרת. הפתרון: המסלול המהיר פתוח רק לפורמטים שגם הדפדפן
  // וגם השרת מכירים. כל השאר (HEIC/HEIF/TIFF/BMP/…) עובר דרך הקנבס
  // ויוצא JPEG — בדיוק אותו מסלול שכל תמונה גדולה עוברת היום.
  // בדפדפן שלא יודע לפתוח HEIC (Chrome/Android) ה-Image נכשל, ואז יש
  // הודעה ברורה במקום "התמונה לא נטענה".
  const PASSTHROUGH_TYPES = /^image\/(jpeg|png|webp)$/i;
  const isHeic = (file) => /^image\/hei[cf]/i.test(file.type || '') || /\.hei[cf]$/i.test(file.name || '');

  function drawTo(img, dim) {
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const k = Math.min(1, dim / Math.max(w, h));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w * k));
    c.height = Math.max(1, Math.round(h * k));
    const x = c.getContext('2d');
    // JPEG לא תומך בשקיפות. בלי רקע לבן מפורש, אזורים שקופים
    // הופכים לשחור.
    x.fillStyle = '#FFFFFF';
    x.fillRect(0, 0, c.width, c.height);
    x.drawImage(img, 0, 0, c.width, c.height);
    return c;
  }

  const toBlob = (canvas, q) => new Promise((res) =>
    canvas.toBlob((b) => res(b), 'image/jpeg', q));

  /**
   * מחזיר { file, report } — הקובץ המוכן להעלאה ותיאור מה נעשה.
   * זורק רק אם התמונה לא נטענת בכלל.
   */
  async function prepare(file) {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      const url = URL.createObjectURL(file);
      i.onload = () => { URL.revokeObjectURL(url); res(i); };
      i.onerror = () => {
        URL.revokeObjectURL(url);
        const e = new Error(isHeic(file) ? 'הדפדפן לא מצליח לפתוח תמונת HEIC' : 'התמונה לא נטענה');
        e.heic = isHeic(file);
        rej(e);
      };
      i.src = url;
    });

    const srcW = img.naturalWidth, srcH = img.naturalHeight;

    // אם הקובץ כבר קטן מספיק ולא ענק במידות — משאירים אותו כמו שהוא.
    // (רק JPEG/PNG/WebP. HEIC וכל פורמט אחר תמיד מקודדים מחדש — ראו למעלה.)
    const passthrough = PASSTHROUGH_TYPES.test(file.type || '') && !isHeic(file);
    if (passthrough && file.size <= BUDGET && Math.max(srcW, srcH) <= MAX_DIM) {
      return {
        file: file,
        report: { changed: false, w: srcW, h: srcH, bytes: file.size, quality: null },
      };
    }

    for (const dim of [MAX_DIM, 1800, 1400, 1100]) {
      const canvas = drawTo(img, dim);
      for (const q of QUALITIES) {
        const blob = await toBlob(canvas, q);
        if (blob && blob.size <= BUDGET) {
          const name = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
          return {
            file: new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }),
            report: {
              changed: true, w: canvas.width, h: canvas.height,
              bytes: blob.size, quality: q, srcW: srcW, srcH: srcH, srcBytes: file.size,
              converted: !passthrough, srcType: file.type || (isHeic(file) ? 'image/heic' : ''),
            },
          };
        }
      }
    }
    throw new Error('לא הצלחנו להקטין את התמונה מתחת למגבלה. נסו תמונה אחרת.');
  }

  const mb = (n) => (n / 1024 / 1024).toFixed(2) + 'MB';

  function describe(r) {
    if (!r.changed) return 'התמונה נשלחה כמו שהיא (' + r.w + '×' + r.h + ', ' + mb(r.bytes) + ').';
    if (r.converted) {
      return 'הומרה ל-JPEG' + (/heic|heif/i.test(r.srcType) ? ' (מ-HEIC)' : '') + ' ' +
             r.srcW + '×' + r.srcH + ' ' + mb(r.srcBytes) + ' → ' + r.w + '×' + r.h + ' ' + mb(r.bytes) + '.';
    }
    return 'הוקטנה ' + r.srcW + '×' + r.srcH + ' ' + mb(r.srcBytes) +
           ' → ' + r.w + '×' + r.h + ' ' + mb(r.bytes) + '.';
  }

  global.PZUpload = { prepare, describe, BUDGET, HARD_LIMIT, mb };
})(window);
